// Pricing from GBFS system_pricing_plans, billed the way Pony does (pony-core get_pricing_plans):
// unlock fee (`price`) + `rate` for every started `interval` of minutes, counted from minute 1.
// Amounts are kept in cents to avoid float drift.

export interface PricingSegment {
  /** 1-based minute at which this rate starts (Pony always sends 1). */
  start: number;
  rateCents: number;
  intervalMin: number;
  /** Last minute covered, if bounded. */
  end?: number;
}

export interface PricingPlan {
  id: string;
  currency: string;
  unlockCents: number;
  segments: PricingSegment[];
}

interface GbfsPlan {
  plan_id: string;
  currency: string;
  price: number;
  per_min_pricing?: { start: number; rate: number; interval: number; end?: number }[];
}

const cents = (euros: number) => Math.round(euros * 100);

export function planFromGbfs(p: GbfsPlan): PricingPlan {
  return {
    id: p.plan_id,
    currency: p.currency,
    unlockCents: cents(p.price),
    segments: (p.per_min_pricing ?? []).map((s) => ({
      start: s.start,
      rateCents: cents(s.rate),
      intervalMin: Math.max(1, s.interval),
      ...(s.end !== undefined && { end: s.end }),
    })),
  };
}

/** Billed minutes: every started minute, at least one once the ride started. */
export const billedMinutes = (startMs: number, endMs: number) =>
  Math.max(1, Math.ceil(Math.max(0, endMs - startMs) / 60_000));

export function priceForPlan(plan: PricingPlan, startMs: number, endMs: number): number {
  const minutes = billedMinutes(startMs, endMs);
  let total = plan.unlockCents;
  for (const s of plan.segments) {
    const from = s.start - 1;
    const to = s.end !== undefined ? Math.min(minutes, s.end) : minutes;
    if (to > from) total += Math.ceil((to - from) / s.intervalMin) * s.rateCents;
  }
  return total;
}

export const formatEuros = (c: number) => `${(c / 100).toFixed(2).replace(".", ",")} €`;

/** "1,00 € + 0,29 € / min", "0,21 € / min, déverrouillage offert", "1,50 € / 20 min, …". */
export function planLabel(plan: PricingPlan): string {
  const s = plan.segments[0];
  const rate = s
    ? `${formatEuros(s.rateCents)} / ${s.intervalMin === 1 ? "min" : `${s.intervalMin} min`}`
    : "";
  if (plan.unlockCents === 0) return rate ? `${rate}, déverrouillage offert` : "Gratuit";
  return rate ? `${formatEuros(plan.unlockCents)} + ${rate}` : formatEuros(plan.unlockCents);
}
